/**
 * Los tipos del dominio.
 *
 * En Postgres eran `create type ... as enum`. En SQLite son `TEXT` con un
 * `CHECK (col in (...))`, y estas listas son la fuente de la que salen esos
 * CHECK: el generador de migraciones las lee, así que la base y el código no
 * pueden divergir.
 *
 * Las etiquetas viven aparte, en `labels.ts`: **el negocio habla español, el
 * código inglés**, y mezclarlos es cómo se termina con «consultorio»
 * hardcodeado en el esquema.
 */

export const APP_ROLE = ['ADMIN', 'CLIENT_ADMIN', 'CLIENT_MEMBER', 'EMPLOYEE'] as const;
export type AppRole = (typeof APP_ROLE)[number];

export const ACCESS_LEVEL = ['MEMBER', 'UNIT_ADMIN'] as const;
export type AccessLevel = (typeof ACCESS_LEVEL)[number];

export const UNIT_STATUS = ['PROSPECT', 'ACTIVE', 'PAUSED', 'ENDED'] as const;
export type UnitStatus = (typeof UNIT_STATUS)[number];

export const UNIT_TYPE = ['CLINIC', 'OFFICE', 'BUILDING', 'STORE', 'COMPLEX', 'OTHER'] as const;
export type UnitType = (typeof UNIT_TYPE)[number];

export const PARTY_KIND = ['MEMBER', 'UNIT', 'EXTERNAL'] as const;
export type PartyKind = (typeof PARTY_KIND)[number];

export const TAX_ID_TYPE = ['FISICA', 'JURIDICA', 'DIMEX', 'NITE'] as const;
export type TaxIdType = (typeof TAX_ID_TYPE)[number];

export const SUBSCRIPTION_STATUS = ['DRAFT', 'ACTIVE', 'PAUSED', 'ENDED', 'CANCELLED'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[number];

export const BILLING_FREQUENCY = [
  'MONTHLY', 'BIMONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL', 'ONE_TIME',
] as const;
export type BillingFrequency = (typeof BILLING_FREQUENCY)[number];

/** Cuántos meses cubre un período de cada frecuencia. */
export const FREQUENCY_MONTHS: Record<BillingFrequency, number> = {
  MONTHLY: 1,
  BIMONTHLY: 2,
  QUARTERLY: 3,
  SEMIANNUAL: 6,
  ANNUAL: 12,
  ONE_TIME: 1,
};

export const PRICE_MODE = ['BASE_PRICE', 'PRICE_OVERRIDE'] as const;
export type PriceMode = (typeof PRICE_MODE)[number];

export const DISCOUNT_TYPE = ['NONE', 'FIXED', 'PERCENTAGE'] as const;
export type DiscountType = (typeof DISCOUNT_TYPE)[number];

/**
 * Dos modos de reparto, no siete.
 *
 * El brief original (§24) traía SINGLE_PAYER, UNIT_PAYER, EQUAL_SPLIT,
 * CUSTOM_PERCENTAGE y FIXED_AMOUNTS como valores distintos, pero los cinco
 * son la misma pregunta — «¿cuánto paga cada quien este mes?» — resuelta
 * distinto según qué campo tenga cada integrante: un porcentaje, un monto
 * fijo, o ninguno (reparto igual). Ahora es una sola cosa, SPLIT, y el campo
 * poblado en cada `subscription_allocation_members` decide el cálculo — no
 * hace falta una enumeración para eso.
 *
 * MONTHLY_ROTATION y CUSTOM_MONTHLY eran la misma idea con distinto nivel de
 * automatismo (con ancla calculada vs. solo overrides). Ahora es ROTATION,
 * y el ancla es siempre requerida — una rotación sin ancla era un modo sin
 * cálculo, no un modo distinto.
 */
export const ALLOCATION_MODE = ['SPLIT', 'ROTATION'] as const;
export type AllocationMode = (typeof ALLOCATION_MODE)[number];

export const CHARGE_STATUS = ['DRAFT', 'ISSUED', 'VOID'] as const;
export type ChargeStatus = (typeof CHARGE_STATUS)[number];

/**
 * De dónde salió un cobro del histórico. La interfaz distingue un cobro real
 * de uno deducido, y nada se inventa para rellenar un mes vacío.
 */
export const PROVENANCE = ['SYSTEM', 'INVOICE', 'CHECKLIST', 'INFERRED', 'UNKNOWN'] as const;
export type Provenance = (typeof PROVENANCE)[number];

/** Dos ejes independientes a propósito (§29): la UI los simplifica, la base no. */
export const PAYMENT_STATUS = [
  'PENDING', 'UNDER_REVIEW', 'PARTIALLY_PAID', 'PAID', 'FAILED', 'REFUNDED',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUS)[number];

export const INVOICE_STATUS = ['NOT_REQUESTED', 'REQUESTED', 'PROCESSING', 'ISSUED', 'FAILED'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUS)[number];

export const PAYMENT_METHOD = [
  'SINPE_MOBILE', 'BANK_TRANSFER', 'MANUAL_TRANSFER', 'ONLINE_GATEWAY', 'APPLE_PAY', 'CASH', 'OTHER',
] as const;
export type PaymentMethod = (typeof PAYMENT_METHOD)[number];

export const SUBMISSION_STATUS = ['SUBMITTED', 'UNDER_REVIEW', 'CONFIRMED', 'REJECTED'] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUS)[number];

export const TXN_SOURCE = [
  'PAYMENT', 'REFUND', 'PURCHASE', 'PAYROLL', 'OVERTIME', 'MANUAL', 'ADJUSTMENT',
] as const;
export type TxnSource = (typeof TXN_SOURCE)[number];

export const TXN_DIRECTION = ['INFLOW', 'OUTFLOW'] as const;
export type TxnDirection = (typeof TXN_DIRECTION)[number];

export const DELIVERY_STATUS = ['PENDING', 'IN_FLIGHT', 'SUCCEEDED', 'FAILED', 'DEAD'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUS)[number];

export const REQUEST_CATEGORY = [
  'EMERGENCY', 'ADDITIONAL_CLEANING', 'DEEP_CLEANING', 'SUPPLIES', 'SPECIAL_REQUEST', 'OTHER',
] as const;
export type RequestCategory = (typeof REQUEST_CATEGORY)[number];

export const REQUEST_STATUS = [
  'OPEN', 'DISPATCHED', 'ACKNOWLEDGED', 'RESOLVED', 'CANCELLED', 'SPAM',
] as const;
export type RequestStatus = (typeof REQUEST_STATUS)[number];

export const PREFERRED_CHANNEL = ['EMAIL', 'WHATSAPP', 'SMS'] as const;
export type PreferredChannel = (typeof PREFERRED_CHANNEL)[number];

export const INCIDENT_STATUS = ['OPEN', 'IN_REVIEW', 'ASSIGNED', 'RESOLVED', 'CLOSED'] as const;
export type IncidentStatus = (typeof INCIDENT_STATUS)[number];

export const INCIDENT_SEVERITY = ['LOW', 'NORMAL', 'HIGH'] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITY)[number];

export const VISIT_STATUS = ['SCHEDULED', 'IN_PROGRESS', 'DONE', 'SKIPPED', 'CANCELLED'] as const;
export type VisitStatus = (typeof VISIT_STATUS)[number];

/**
 * Las dos capas de visita (0024, decisión del dueño del 22-ago-2026).
 *
 * `BASE` es la ronda garantizada: una por consultorio por día, y la base tiene
 * prioridad sobre cualquier otra cosa. `EXTRA` es la que pide el cliente, y
 * solo entra en la capacidad que sobra después de cubrir el mínimo de todos.
 *
 * No es una etiqueta cosmética: sin ella no hay forma de contestar «¿todos los
 * consultorios tienen su visita de hoy?», que es la pregunta que dice si el
 * servicio se cumplió.
 */
export const VISIT_KIND = ['BASE', 'EXTRA'] as const;
export type VisitKind = (typeof VISIT_KIND)[number];

/**
 * De qué es una obligación propia de la empresa (0019).
 *
 * No son categorías contables —para eso está `financial_categories`, que el
 * dueño edita— sino los cajones con los que uno piensa la agenda de pagos:
 * «esto es impuesto», «esto es planilla». Por eso son fijos y viven acá.
 */
export const OBLIGATION_CATEGORY = [
  'TAXES', 'PAYROLL', 'UTILITIES', 'RENT', 'INSURANCE', 'OTHER',
] as const;
export type ObligationCategory = (typeof OBLIGATION_CATEGORY)[number];

/** `CANCELLED` = «este mes no aplicaba». No es un borrado: queda con su motivo. */
export const OBLIGATION_STATUS = ['PENDING', 'PAID', 'CANCELLED'] as const;
export type ObligationStatus = (typeof OBLIGATION_STATUS)[number];

export const DOCUMENT_KIND = [
  'PAYMENT_RECEIPT', 'INVOICE_PDF', 'INVOICE_XML', 'CONTRACT', 'ID', 'SERVICE_EVIDENCE', 'OTHER',
] as const;
export type DocumentKind = (typeof DOCUMENT_KIND)[number];

/** Para el `CHECK (col in (...))` de las migraciones. */
export function sqlCheck(column: string, values: readonly string[]): string {
  return `check (${column} in (${values.map((v) => `'${v}'`).join(', ')}))`;
}
